import { Meta } from "components/atoms/Meta";
import { getStrapiImageByBreakpoint } from "./blog";
import { BlogDataQuery } from "gql";
import { YuLifeLocale } from "@hooks";

export function getBlogMetaData(
  {
    title,
    featuredImage,
    excerpt,
    metaDescription,
    metaTitle,
    slug,
    publishDate,
    updatedAt,
    author,
    post,
  }: BlogDataQuery["blogPosts"]["data"][number]["attributes"],
  locale: YuLifeLocale,
  contentType = "article",
) {
  const hasYuLifeSecondaryTitle = /\|\sYuLife/i.test(metaTitle);

  let url: string;

  switch (contentType) {
    case "seo-page":
      url = slug;
      break;
    case "vacancy":
      url = `careers/${slug}`;
      break;
    case "article":
    default:
      url = `blog/${slug}`;
      break;
  }

  // construct canonical URL following the same pattern as Meta component
  const canonicalUrl = process.env.BASE_URL + (locale === "uk" ? "" : `/${locale}`) + `/${url}`;

  const metadata = {
    description: metaDescription || excerpt,
    image: featuredImage.data?.attributes?.formats
      ? getStrapiImageByBreakpoint(featuredImage.data.attributes.formats, "xlarge")
      : undefined,
    imageAlt: metaTitle || title,
    noIndex: false,
    title: hasYuLifeSecondaryTitle ? metaTitle : `${metaTitle || title} | YuLife`,
    url,
  };

  return (
    <Meta
      locale={locale}
      {...metadata}
      schema={JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": canonicalUrl,
        },
        headline: metaTitle || title,
        description: metaDescription || excerpt,
        image: metadata.image,
        ...(author?.data?.attributes && {
          author: {
            "@type": "Person",
            name: author.data.attributes.name,
            ...(author.data.attributes.bio && {
              description: author.data.attributes.bio,
            }),
            ...(author.data.attributes.image?.data?.attributes?.formats && {
              image: getStrapiImageByBreakpoint(
                author.data.attributes.image.data.attributes.formats,
                "medium",
              ),
            }),
          },
        }),
        publisher: {
          "@type": "Organization",
          name: "YuLife",
          logo: {
            "@type": "ImageObject",
            url: "https://yulife.com/static/logos/yulife-small-P600.svg",
          },
        },
        ...(publishDate && {
          datePublished: new Date(publishDate).toISOString().split("T")[0],
        }),
        ...(updatedAt && {
          dateModified: new Date(updatedAt).toISOString().split("T")[0],
        }),
        articleBody: post,
      })}
    />
  );
}
